import { AfterViewInit, Component, OnDestroy, ViewChild } from '@angular/core';
import { HeaderComponent } from '@shared/components/header/header.component';
import { TranslatePipe } from '@ngx-translate/core';
import { TableComponent } from '@shared/components/table/table.component';
import { meteringPointTable } from '@shared/table-configs/meteringpoint/meteringpoint.table';
import { TableConfig } from '@shared/interfaces/table/table-config';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AuthRoles,
  MeteringPointQueryResultDto,
  MeteringPointService,
  MeteringPointType,
  PhysicalStatus,
} from '@app/api/dmc';
import { HeaderService } from '@app/core/services/header.service';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CardComponent } from '@shared/components/card/card.component';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { InputDirective } from '@shared/components/input/input/input.directive';
import { enumToKeyValue } from '@app/core/utils/enum-to-key-value';
import { SelectDirective } from '@shared/components/input/select/select.directive';
import { ButtonDirective } from '@shared/components/input/button/button.directive';
import { RootModalComponent } from '@shared/modals/meteringpoint/root-modal/root-modal.component';
import { ModalService } from '@app/core/services/modal.service';
import {
  CloseMeteringpointComponent
} from '@shared/modals/meteringpoint/close-meteringpoint/close-meteringpoint.component';
import { meteringpointListActionMenu } from '@views/meteringpoint/meteringpoint-list/meteringpoint-list.action-menu';
import { KeyValue } from '@shared/interfaces/key-value/key-value';
import { Observable, Subscription } from 'rxjs';
import { removeNullKeys } from '@app/core/utils/remove-null-keys';
import {
  EditMeteringPointModalService
} from '@shared/modals/meteringpoint/edit-meteringpoint-modal/edit-meteringpoint-modal.service';
import { MenuAction } from '@shared/interfaces/header/menu-action';
import { copyToClipboard } from '@app/core/utils/copy-to-clipboard';
import { ToastService } from '@app/core/services/toast.service';
import { SortParam } from '@app/core/constants/sort-param';
import { hasRole } from '@app/core/guards/has-role.guard';


@Component({
  imports: [
    HeaderComponent,
    TranslatePipe,
    TableComponent,
    CardComponent,
    InputGroupComponent,
    LabelDirective,
    ReactiveFormsModule,
    InputDirective,
    SelectDirective,
    ButtonDirective,
  ],
  templateUrl: './meteringpoint-list.component.html',
})
export class MeteringpointListComponent implements AfterViewInit, OnDestroy {
  @ViewChild('table', { static: false }) public table?: TableComponent<any>;

  /** Search Form **/
  public searchForm = new FormGroup({
    identification: new FormControl<string | null>(null),
    streetName: new FormControl<string | null>(null),
    buildingNumber: new FormControl<string | null>(null),
    floorIdentification: new FormControl<string | null>(null),
    postalCode: new FormControl<string | null>(null),
    meteringPointType: new FormControl<MeteringPointType | null>(null),
    physicalStatus: new FormControl<PhysicalStatus | null>(null),
    meterNumber: new FormControl<string | null>(null),
  });

  /** Dropdown Values **/
  public meteringPointTypeOptions: KeyValue[] = enumToKeyValue(MeteringPointType, { omitKeys: ['NotSpecified'], prefixValue: 'enum.meteringpoint.meteringpointtype.' });
  public physicalStatusOptions: KeyValue[] = enumToKeyValue(PhysicalStatus, { omitKeys: ['NotSpecified'], prefixValue: 'enum.meteringpoint.physicalstatus.' });

  /** Actions **/
  public mpWriter = hasRole([AuthRoles.MeteringPointWriter]);
  public actions: MenuAction[] = [
    {
      ...meteringpointListActionMenu[0],
      callback: () => this.openCreateModal(),
      disabled: () => !this.mpWriter,
    },
    {
      ...meteringpointListActionMenu[1],
      callback: () => this.openEditModal(),
      disabled: () => !this.mpWriter || this.selectedElements.length !== 1 || (this.selectedElements[0]?.dataHubStatus === 'Rejected' && this.selectedElements[0]?.existsInDataHub) || this.selectedElements[0]?.dataHubStatus === 'Pending' || this.selectedElements[0]?.dataHubStatus === 'PendingJob',
    },
    {
      ...meteringpointListActionMenu[2],
      callback: () => this.openMasterDataModal(),
      disabled: () => !this.mpWriter
    },
    {
      ...meteringpointListActionMenu[3],
      callback: () => this.openShutdownModal(),
      disabled: () => !this.mpWriter || this.selectedElements.length !== 1,
    }
  ]

  private readonly subscriptions = new Subscription();

  private static readonly COLUMNS: (keyof MeteringPointQueryResultDto)[] = [
    'identification', 'address', 'settlementMethod', 'meteringPointType', 'physicalStatus',
    'meterNumber', 'isMeterActive', 'dataHubStatus', 'hasBalanceSupplier', 'isChild', 'existsInDataHub',
  ];

  /** Table Config **/
  public meterTableConfig: TableConfig<MeteringPointQueryResultDto> = {
    ... meteringPointTable,
    activeColumns: MeteringpointListComponent.COLUMNS,
    serviceCallback: (pageSize: number, page: number, order?: SortParam) => this.search(pageSize, page, order),
    contextActions: [
      ...this.actions.slice(1),
      {
        id: 'copy',
        title: 'common.copy',
        breaker: true,
        callback: () => this.copyToClipboard()
      }
    ]
  };

  /** Selected Table Elements **/
  public selectedElements: MeteringPointQueryResultDto[] = [];

  constructor(
    private readonly router: Router,
    private readonly meteringPointService: MeteringPointService,
    private readonly headerMenuService: HeaderService,
    private readonly modalService: ModalService,
    private readonly route: ActivatedRoute,
    private readonly toastService: ToastService,
    private readonly editMeteringPointModalService: EditMeteringPointModalService,
  ) {
    this.headerMenuService.setActions(this.actions);
  }

  public ngAfterViewInit(): void {
    this.subscriptions.add(
      this.route.queryParams.subscribe(() => {
        this.populateFormFromParams();
      })
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private populateFormFromParams(): void {
    const queryParams = this.route.snapshot.queryParams;
    if (Object.keys(queryParams).length > 0) {
      this.searchForm.patchValue(queryParams);
      this.searchForm.markAsDirty();
      this.table?.search();
    }
  }

  /**
   * Search for metering points - fetched by the table.
   * @param pageSize
   * @param page
   * @param order
   * @returns
   */
  public search(pageSize: number, page: number, order?: SortParam): Observable<any> {
    const sort = order ? { SortParams: [order] } : {};
    const query = removeNullKeys(this.searchForm.value);
    return this.meteringPointService.meteringPointQueryFromSearchPagePost({ ...query, page, pageSize, ...sort });
  }

  public openShutdownModal(): void {
    if (this.selectedElements.length !== 1) return;

    const id: string | undefined = this.selectedElements[0].identification;

    this.modalService.show({
      component: CloseMeteringpointComponent,
      title: 'meteringPoint.action.shutdown.title',
      actions: [
        { type: 'primary', label: 'system.button.save', method: 'submit' },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ],
      params: { id }
    }).subscribe(() => {
      this.table?.search();
    })
  }

  private copyToClipboard(): void {
    if (this.selectedElements.length !== 1) return;

    if (this.selectedElements[0].identification) {
      void copyToClipboard(this.selectedElements[0].identification).then(() => {
        this.toastService.show('meteringPoint.action.copy.success');
      });
    }
  }

  public openCreateModal(): void {
    this.router.navigate(['meteringpoint', 'create']);
  }

  public openEditModal(): void {
    const identification = this.selectedElements[0]?.identification;
    if (!identification) return;

    this.editMeteringPointModalService.open(identification).subscribe(() => {
      this.table?.search();
    })
  }

  public openMasterDataModal(): void {
    this.modalService.show({
      component: RootModalComponent,
      title: 'meteringpoint.root.title',
      actions: [
        { type: 'primary', label: 'system.button.request', method: 'submit' },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ],
      params: { id: this.selectedElements[0]?.identification }
    });
  }

  public performSearch(): void {
    this.table?.search();
    this.updateUriWithParams();
  }

  private updateUriWithParams(): void {
    const rawValues = this.searchForm.getRawValue();
    const queryParams: { [key: string]: string | null } = {};

    for (const [key, value] of Object.entries(rawValues)) {
      queryParams[key] = (value === null || value === undefined || value === '') ? null : value as string;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
    });
  }

  public clearForm(): void {
    this.searchForm.reset();

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
    });
  }
}
